import type { ReactNode } from 'react'
import type { ResourcesDto } from '@/api/hooks'
import { useResourceTick } from '@/hooks/useResourceTick'
import { GoldIcon, WoodIcon, RumIcon, PennantIcon } from '@/components/game/GameIcons'
import { HintBubble } from '@/components/game/ui'

const RESOURCE_META = {
  gold: { Icon: GoldIcon, label: 'Or' },
  wood: { Icon: WoodIcon, label: 'Bois' },
  rum: { Icon: RumIcon, label: 'Rhum' },
} as const

/**
 * Taux horaire signé. Le libellé codait « +{perHour}/h » en dur, ce qui rendait
 * « + -30/h » dès que l'entretien du branle-bas fait passer un taux en négatif.
 */
function fmtRate(perHour: number): string {
  return `${perHour < 0 ? '−' : '+'}${Math.abs(perHour).toLocaleString('fr-FR')}/h`
}

export function ResourceBar({ resources }: { resources: ResourcesDto }) {
  // Entretien du branle-bas, par ressource : la production brute moins ce qui
  // s'accumule réellement. Le bois n'est jamais concerné.
  const upkeep = resources.mobilized
    ? {
        gold: Math.max(0, (resources.goldProductionPerHour ?? resources.goldPerHour) - resources.goldPerHour),
        rum: Math.max(0, (resources.rumProductionPerHour ?? resources.rumPerHour) - resources.rumPerHour),
      }
    : null

  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-3">
      <ResourceChip
        meta={RESOURCE_META.gold}
        base={resources.gold}
        perHour={resources.goldPerHour}
        capacity={resources.capacity}
        production={resources.goldProductionPerHour}
        upkeep={upkeep?.gold ?? 0}
      />
      <ResourceChip meta={RESOURCE_META.wood} base={resources.wood} perHour={resources.woodPerHour} capacity={resources.capacity} />
      <ResourceChip
        meta={RESOURCE_META.rum}
        base={resources.rum}
        perHour={resources.rumPerHour}
        capacity={resources.capacity}
        production={resources.rumProductionPerHour}
        upkeep={upkeep?.rum ?? 0}
      />
    </div>
  )
}

function ResourceChip({
  meta,
  base,
  perHour,
  capacity,
  production,
  upkeep = 0,
}: {
  meta: { Icon: (p: { className?: string }) => ReactNode; label: string }
  base: number
  perHour: number
  capacity: number
  /** Production brute, avant entretien. Absente sur le bois, jamais concerné. */
  production?: number
  /** Entretien du branle-bas prélevé sur cette ressource, 0 au mouillage. */
  upkeep?: number
}) {
  const value = useResourceTick(base, perHour, capacity)
  const atCapacity = value >= capacity
  const pct = capacity > 0 ? Math.min(100, (value / capacity) * 100) : 0
  const { Icon } = meta

  return (
    <div className="min-w-0 rounded-xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-2.5 sm:p-3">
      <div className="flex items-center justify-between gap-1 min-w-0">
        <span className="flex items-center gap-1.5 min-w-0 text-[13px] sm:text-sm text-ocean-200">
          <Icon className="w-4 h-4 shrink-0 text-sand-300" />
          <span className="truncate">{meta.label}</span>
        </span>
        {/* Le taux affiché est le NET, seul chiffre qui compte pour le joueur.
            Le détail du calcul vit dans une bulle : une décomposition en clair
            sur la carte se lisait « branle-bas −45/h sur 66/h », où le « sur 66/h »
            passait pour une durée de 66 heures. Ambre quand l'entretien mord,
            jamais rouge : le rouge d'un chiffre veut dire « entrepôt plein ». */}
        {upkeep > 0 ? (
          <span className="group/tip relative shrink-0 inline-flex items-center gap-1 text-[10px] sm:text-xs text-amber-300 tabular-nums">
            <PennantIcon className="w-3 h-3 shrink-0" aria-hidden="true" />
            {fmtRate(perHour)}
            <HintBubble
              align="end"
              label={
                <span className="block space-y-0.5 tabular-nums">
                  <span className="block">
                    Production {fmtRate(production ?? perHour + upkeep)}
                  </span>
                  <span className="block text-amber-300">
                    Branle-bas −{upkeep.toLocaleString('fr-FR')}/h
                  </span>
                  <span className="block border-t border-sand-500/25 pt-0.5 text-sand-200">
                    Net {fmtRate(perHour)}
                  </span>
                </span>
              }
            />
          </span>
        ) : (
          <span className="shrink-0 text-[10px] sm:text-xs text-ocean-300 tabular-nums">{fmtRate(perHour)}</span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-1">
        <span className={`font-display text-lg sm:text-xl tabular-nums ${atCapacity ? 'text-red-400' : 'text-sand-300'}`}>
          {value.toLocaleString('fr-FR')}
        </span>
        <span className="text-xs text-ocean-300 tabular-nums">/ {capacity.toLocaleString('fr-FR')}</span>
      </div>
      <div className="mt-1.5 h-1 bg-ocean-900 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-1000 ${atCapacity ? 'bg-rum-500' : 'bg-ocean-300'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}
